import { useMemo } from 'react';

/** A person who can be picked for matter-scoped work (rate override, time entry). */
export interface MatterCollaborator {
  /**
   * Canonical identifier sent as `user_uuid` to the rate-override / time-entry
   * APIs (uuid-first writes, see MATTER_ECONOMICS_API.md). Derived from the
   * matter collaborator's `collaborator_id` (which is the user UUID).
   */
  user_uuid: string;
  /** Secondary, read-only numeric id. Absent for collaborators that only expose a UUID. */
  user_id?: string;
  name: string;
  email: string;
  avatar?: string | null;
  initials?: string | null;
  isOwner: boolean;
}

/**
 * Derive a matter's people from `matter.collaborators` PLUS the matter owner —
 * the owner isn't always present in the collaborators array (tag matter-economics).
 *
 * The matter API returns the populated list under the lowercase `collaborators`
 * key (objects: `{ collaborator_id, name, email, avatar_url, initials, ... }`,
 * where `collaborator_id` is the user UUID); the legacy uppercase `Collaborators`
 * (`{ UserID, FullName, Email }`) is the fallback. `user_uuid` prefers an explicit
 * uuid field, then `collaborator_id`; `user_id` keeps any numeric id.
 */
export function useMatterCollaborators(matter: any): MatterCollaborator[] {
  return useMemo(() => {
    if (!matter) return [];
    const out: MatterCollaborator[] = [];
    const seen = new Set<string>();

    const push = (
      uuid: unknown,
      userId: unknown,
      name: string,
      email: string,
      avatar: unknown,
      initials: unknown,
      isOwner: boolean,
    ) => {
      const uid = uuid != null && uuid !== '' ? String(uuid) : '';
      // Dedup on the uuid when present, else on the numeric id.
      const key = uid || (userId != null ? `id:${userId}` : '');
      if (!key) return;
      if (seen.has(key)) {
        if (isOwner) {
          const existing = out.find((c) => (c.user_uuid || `id:${c.user_id}`) === key);
          if (existing) existing.isOwner = true;
        }
        return;
      }
      seen.add(key);
      out.push({
        user_uuid: uid,
        user_id: userId != null && userId !== '' ? String(userId) : undefined,
        name: name?.trim() || email || (uid ? `User ${uid}` : `User ${userId}`),
        email: email || '',
        avatar: (avatar as string) || null,
        initials: (initials as string) || null,
        isOwner,
      });
    };

    // Owner first (created_by_user object if present, else the matter's UserID).
    const creator = matter.created_by_user || matter.creator;
    if (creator) {
      const cname = creator.full_name || creator.name || `${creator.first_name || ''} ${creator.last_name || ''}`.trim();
      push(
        creator.user_uuid ?? creator.uuid ?? creator.UserUID,
        creator.user_id ?? creator.id ?? matter.UserID,
        cname,
        creator.email,
        creator.avatar_url || creator.ProfilePicture,
        creator.initials,
        true,
      );
    } else if (matter.UserID) {
      push(
        matter.created_by_uuid ?? matter.owner_uuid,
        matter.UserID,
        matter.created_by_name || '',
        matter.created_by_email || '',
        null,
        null,
        true,
      );
    }

    // Collaborators - prefer the populated lowercase array (collaborator_id = user UUID).
    const list = (matter.collaborators?.length ? matter.collaborators : matter.Collaborators) || [];
    list.forEach((c: any) => {
      const uuid = c.user_uuid ?? c.uuid ?? c.collaborator_id ?? c.UserUUID;
      const userId = c.user_id ?? c.UserID;
      const name = c.name || c.FullName || c.full_name || `${c.first_name || ''} ${c.last_name || ''}`.trim();
      push(uuid, userId, name, c.email || c.Email, c.avatar_url || c.avatarURL || c.ProfilePicture, c.initials, false);
    });

    return out;
  }, [matter]);
}

export default useMatterCollaborators;
